// SavWar Ana Sayfa - Günlük Kelime Savaşları
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiDelete, apiPatch, API_BASE_URL } from '../lib/api';
import { useAuth } from '../context/AuthProvider';
import RankTooltip from '../components/RankTooltip';
import Avatar from '../components/Avatar';
import RichText from '../components/RichText';
import { UserBadgeList } from '../components/UserBadge';
import { TPGainToast, EffectToast } from '../components/EffectToast';
import { RankUpModal } from '../components/RankUpModal';
import { useTranslation } from 'react-i18next';

// Türkçe karakterleri normalize et (backend ile uyumlu)
const normalizeSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9_-]/g, ''); // Sadece a-z, 0-9, _, - karakterleri bırak
};

interface Author {
  _id: string;
  username: string;
  avatarUrlSmall?: string;
  savwarTP: number;
  savwarRank: number;
  roles?: string[];
  role?: "user" | "vip" | "moderator" | "admin";
  isAdmin?: boolean;
}

interface Post {
  _id: string;
  sessionId: string;
  authorId: Author;
  content: string;
  starsCount: number;
  commentsCount: number;
  locked: boolean;
  createdAt: string;
  hasStarred?: boolean;
  _rankChange?: 'up' | 'down';
}

interface Session {
  _id: string;
  topicTag: string;
  startAt: string;
  endAt: string;
  active: boolean;
  stats: {
    posts: number;
    stars: number;
    commenters: number;
  };
}

interface FinishedSession {
  session: Session;
  top3: Post[];
}

export default function SavWarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [displayCount, setDisplayCount] = useState(25);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userPost, setUserPost] = useState<Post | null>(null);
  const [userPostRank, setUserPostRank] = useState<number | null>(null);
  const [finishedSession, setFinishedSession] = useState<FinishedSession | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [postContent, setPostContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showTpToast, setShowTpToast] = useState(false);
  const [tpGained, setTpGained] = useState(0);
  const [showRankUpModal, setShowRankUpModal] = useState(false);
  const [rankUpData, setRankUpData] = useState<{ oldRank: number; newRank: number } | null>(null);
  
  // Rütbe sıralaması state
  const [showRankLeaderboard, setShowRankLeaderboard] = useState(false);
  const [rankLeaderboard, setRankLeaderboard] = useState<Array<{
    userId: string;
    username: string;
    savwarTP: number;
    savwarRank: number;
    avatarUrlSmall?: string;
  }>>([]);
  const [loadingRankLeaderboard, setLoadingRankLeaderboard] = useState(false);

  useEffect(() => {
    loadActiveSession();
  }, []);

  // Geri sayım timer
  useEffect(() => {
    if (!session?.active) return;

    const timer = setInterval(() => {
      const now = Date.now();
      const end = new Date(session.endAt).getTime();
      const remaining = Math.max(0, end - now);
      setTimeRemaining(remaining);

      if (remaining === 0) {
        loadActiveSession(); // Süre doldu, yeniden yükle
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [session]);

  const loadActiveSession = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: any = await apiGet('/api/savwar/active');
      
      console.log('[SavWar] Active session response:', response);
      
      if (response && response.success && response.data) {
        if (response.data.session) {
          // Backend'den topicId.tag geliyor, topicTag'e map et
          const sessionData = {
            ...response.data.session,
            topicTag: response.data.session.topicId?.tag || response.data.session.topicTag || ''
          };
          console.log('[SavWar] Mapped session data:', sessionData);
          console.log('[SavWar] topicTag:', sessionData.topicTag);
          setSession(sessionData);
          setPosts(response.data.posts || []);
          setUserPost(response.data.userPost || null);
          setUserPostRank(response.data.userPostRank || null);
          setTimeRemaining(response.data.timeRemaining || 0);
          setFinishedSession(null);
        } else {
          // Aktif oturum yok, son bitmiş oturumu getir
          setSession(null);
          setPosts([]);
          setUserPost(null);
          setUserPostRank(null);
          
          const finishedResponse: any = await apiGet('/api/savwar/latest-finished');
          if (finishedResponse && finishedResponse.success && finishedResponse.data.session) {
            // Backend'den topicId.tag geliyor, topicTag'e map et
            const finishedData = {
              ...finishedResponse.data,
              session: {
                ...finishedResponse.data.session,
                topicTag: finishedResponse.data.session.topicId?.tag || finishedResponse.data.session.topicTag || ''
              }
            };
            setFinishedSession(finishedData);
          } else {
            setFinishedSession(null);
          }
        }
      }
    } catch (err: any) {
      console.error('[SavWar] Load error:', err);
      setError('Oturum yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setDisplayCount(25);
    await loadActiveSession();
  };

  const handleLoadMore = () => {
    if (loadingMore || displayCount >= posts.length) return;
    setLoadingMore(true);
    setTimeout(() => {
      setDisplayCount(prev => Math.min(prev + 25, posts.length));
      setLoadingMore(false);
    }, 300);
  };

  const handleSubmitPost = async () => {
    if (!session || !postContent.trim()) return;

    const keyword = session.topicTag.toLowerCase();
    if (!postContent.toLowerCase().includes(keyword)) {
      alert(`Postunuz "${session.topicTag}" kelimesini içermelidir.`);
      return;
    }

    try {
      setSubmitting(true);
      const response: any = await apiPost(`/api/savwar-posts/sessions/${session._id}/post`, {
        content: postContent.trim(),
      });

      if (response && response.success && response.data) {
        alert('Başarılı! Postunuz oluşturuldu. -10 SavLike');
        setUserPost(response.data.post);
        setPostContent('');
        await loadActiveSession();
      }
    } catch (err: any) {
      const errorData = err.data || err.response?.data || {};
      const msg = errorData.error || err.message || 'Post oluşturulamadı';
      const code = errorData.code || err.code;
      
      if (code === 'INSUFFICIENT_BALANCE') {
        const balanceData = errorData.data || {};
        alert(
          `Yetersiz SavLike\n\nPost oluşturmak için ${balanceData.required || 10} yıldıza ihtiyacınız var.\nMevcut bakiyeniz: ${balanceData.current || 0} yıldız\n\nDiğer kullanıcıların postlarını beğenerek veya yorum yaparak SavLike kazanabilirsiniz!`
        );
      } else {
        alert(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPost = () => {
    if (!userPost) return;
    setEditMode(true);
    setEditContent(userPost.content);
  };

  const handleSaveEdit = async () => {
    if (!session || !userPost || !editContent.trim()) return;

    const keyword = session.topicTag.toLowerCase();
    if (!editContent.toLowerCase().includes(keyword)) {
      alert(`Postunuz "${session.topicTag}" kelimesini içermelidir.`);
      return;
    }

    try {
      setSubmitting(true);
      const response: any = await apiPatch(`/api/savwar-posts/posts/${userPost._id}`, {
        content: editContent.trim(),
      });

      if (response.success) {
        alert('Başarılı! Postunuz güncellendi.');
        setEditMode(false);
        await loadActiveSession();
      }
    } catch (err: any) {
      alert('Post güncellenemedi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditContent('');
  };

  const handleDeletePost = async () => {
    if (!userPost) return;
    if (!confirm('Postunuzu silmek istediğinize emin misiniz? Yeniden oluşturabilirsiniz.')) return;

    const deletedPostId = userPost._id;
    const deletedAuthorId = userPost.authorId._id;
    const tpToRevoke = (userPost.starsCount * 1) + (userPost.commentsCount * 0.2);
    
    try {
      // Optimistic update
      setUserPost(null);
      setUserPostRank(null);
      
      setPosts(prevPosts => {
        const filtered = prevPosts.filter(p => p._id !== deletedPostId);
        
        const updated = filtered.map(p => {
          if (p.authorId._id === deletedAuthorId) {
            const newTP = Math.max(0, (p.authorId.savwarTP || 0) - tpToRevoke);
            return {
              ...p,
              authorId: {
                ...p.authorId,
                savwarTP: newTP,
              },
            };
          }
          return p;
        });
        
        return updated.sort((a, b) => {
          if (b.starsCount !== a.starsCount) return b.starsCount - a.starsCount;
          if (b.commentsCount !== a.commentsCount) return b.commentsCount - a.commentsCount;
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
      });
      
      await apiDelete(`/api/savwar-posts/posts/${deletedPostId}`);
    } catch (err: any) {
      alert('Post silinemedi');
      await loadActiveSession();
    }
  };

  const handleToggleStar = async (postId: string) => {
    if (!session?.active) {
      alert('Sadece aktif oturumlarda yıldız verebilirsiniz');
      return;
    }

    if (!user) {
      alert('Yıldız vermek için giriş yapmalısınız');
      return;
    }

    try {
      const post = posts.find(p => p._id === postId);
      if (!post) return;

      const isAdmin = (user as any)?.isAdmin || false;
      const hasStarred = post.hasStarred;
      const willAddStar = isAdmin ? true : !hasStarred;
      const tpChange = willAddStar ? +1 : -1;

      // TP toast kaldırıldı - yıldız veren değil, yıldız alan TP kazanır
      // Bildirim sistemi post sahibine otomatik gösterir

      // Optimistic update
      setPosts(prevPosts => {
        const oldRanks = new Map(prevPosts.map((p, idx) => [p._id, idx]));
        
        const updatedPosts = prevPosts.map(p => {
          if (p._id === postId) {
            const newTP = (p.authorId.savwarTP || 0) + tpChange;
            return {
              ...p,
              hasStarred: willAddStar,
              starsCount: willAddStar ? p.starsCount + 1 : p.starsCount - 1,
              authorId: {
                ...p.authorId,
                savwarTP: newTP,
              },
            };
          }
          return p;
        });
        
        const sorted = updatedPosts.sort((a, b) => {
          if (b.starsCount !== a.starsCount) return b.starsCount - a.starsCount;
          if (b.commentsCount !== a.commentsCount) return b.commentsCount - a.commentsCount;
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
        
        return sorted.map((p, newIdx) => {
          const oldIdx = oldRanks.get(p._id);
          if (oldIdx !== undefined && oldIdx !== newIdx) {
            return {
              ...p,
              _rankChange: oldIdx > newIdx ? 'up' : 'down' as 'up' | 'down',
            };
          }
          return p;
        });
      });

      if (userPost && userPost.authorId._id === post.authorId._id) {
        setUserPost(prev => prev ? {
          ...prev,
          hasStarred: willAddStar,
          starsCount: willAddStar ? prev.starsCount + 1 : prev.starsCount - 1,
          authorId: {
            ...prev.authorId,
            savwarTP: (prev.authorId.savwarTP || 0) + tpChange,
          },
        } : null);
      }

      setTimeout(() => {
        setPosts(prevPosts => prevPosts.map(p => ({ ...p, _rankChange: undefined })));
      }, 2000);

      if (!willAddStar) {
        await apiDelete(`/api/savwar-posts/posts/${postId}/star`);
      } else {
        await apiPost(`/api/savwar-posts/posts/${postId}/star`, {});
      }
    } catch (err: any) {
      const errorData = err.data || err.response?.data || {};
      const msg = errorData.error || err.message || 'İşlem başarısız';
      const code = errorData.code || err.code;
      
      if (code === 'INSUFFICIENT_BALANCE') {
        const balanceData = errorData.data || {};
        alert(
          `Yetersiz SavLike\n\nYıldız vermek için ${balanceData.required || 1} yıldıza ihtiyacınız var.\nMevcut bakiyeniz: ${balanceData.current || 0} yıldız`
        );
      } else {
        alert(msg);
      }
      await loadActiveSession(); // Rollback
    }
  };

  const formatTimeRemaining = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const resolveAvatar = (url?: string) => {
    if (!url) return undefined;
    return url.startsWith('/') ? `${API_BASE_URL}${url}` : url;
  };

  // Hashtag'leri tıklanabilir yapar
  const renderContentWithHashtags = (content: string) => {
    const hashtagRegex = /(#[a-zA-ZğüşıöçĞÜŞİÖÇ]+)(?=\s|[.,!?;:]|$)/g;
    const parts = content.split(hashtagRegex);

    return parts.map((part, index) => {
      if (part.match(hashtagRegex)) {
        return (
          <span
            key={index}
            style={{
              color: '#114232',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              const slug = normalizeSlug(part.replace('#', ''));
              navigate(`/savwar/topics/${slug}`);
            }}
          >
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const loadRankLeaderboard = async () => {
    try {
      setLoadingRankLeaderboard(true);
      const response: any = await apiGet('/api/savwar/rank-leaderboard');

      const users = response?.data?.users ?? response?.users;

      if (Array.isArray(users)) {
        setRankLeaderboard(users);
        setShowRankLeaderboard(true);
      } else {
        console.warn('[SavWar] Rank leaderboard response missing users array', response);
      }
    } catch (err: any) {
      console.error('[SavWar] Rank leaderboard error:', err);
      alert('Rütbe sıralaması yüklenemedi');
    } finally {
      setLoadingRankLeaderboard(false);
    }
  };

  // Rütbe listesi kullanıcı kartı
  const renderRankUser = (rankUser: any) => (
    <div
      key={rankUser.userId}
      onClick={() => navigate(`/u/${rankUser.username}`)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        border: '1px solid #2c2c2c',
        borderRadius: 8,
        marginBottom: 8,
        cursor: 'pointer',
        background: rankUser.userId === user?.id ? '#1a1a3a' : '#0f0f0f',
      }}
    >
      <div style={{ minWidth: 32, fontWeight: 'bold', color: '#FFD700' }}>
        #{rankUser.globalIndex + 1}
      </div>
      
      {/* Rütbe İkonu */}
      {rankUser.savwarRank > 0 && (
        <div style={{ fontSize: 32 }}>
          <RankTooltip 
            rank={rankUser.savwarRank} 
            tp={rankUser.savwarTP} 
            username={rankUser.username}
          />
        </div>
      )}
      
      <Avatar
        src={resolveAvatar(rankUser.avatarUrlSmall)}
        alt={rankUser.username}
        size={32}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 'bold' }}>@{rankUser.username}</div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          🗡️ {rankUser.savwarTP.toFixed(2)} TP
        </div>
      </div>
    </div>
  );

  const getRankCategory = (rank: number) => {
    if (rank >= 19) return { title: '⭐ GENERALLER', color: '#ffd700' };
    if (rank >= 12) return { title: '🎖️ SUBAYLAR', color: '#c0c0c0' };
    if (rank >= 5) return { title: '🎗️ ASTSUBAYLAR', color: '#cd7f32' };
    return { title: '🎯 ERLER', color: '#8b8b8b' };
  };

  const renderRankModal = () => {
    if (!showRankLeaderboard) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header - Sticky */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '20px 24px',
          background: '#0a0a0a',
          borderBottom: '1px solid #2c2c2c',
          flexShrink: 0,
        }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>🏆 Rütbe Sıralaması</h2>
          <button 
            onClick={() => setShowRankLeaderboard(false)} 
            style={{ 
              fontSize: 24, 
              background: 'transparent', 
              border: 'none', 
              color: '#fff', 
              cursor: 'pointer', 
              padding: '8px 12px',
              borderRadius: 8,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#1a1a1a'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ 
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '24px 16px',
          paddingBottom: '200px',
        }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>

          {rankLeaderboard.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', opacity: 0.7 }}>
              Henüz rütbe kazanan kullanıcı yok.
            </div>
          ) : (
            <div>
              {/* Generaller (19-23) */}
              {rankLeaderboard.some(u => u.savwarRank >= 19) && (
                <>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', 
                    padding: '8px 16px', 
                    borderRadius: 8, 
                    marginBottom: 12,
                    fontWeight: 'bold',
                    color: '#000'
                  }}>
                    ⭐ GENERALLER
                  </div>
                  {rankLeaderboard
                    .map((rankUser, index) => ({ ...rankUser, globalIndex: index }))
                    .filter(u => u.savwarRank >= 19)
                    .map(renderRankUser)}
                </>
              )}

              {/* Subaylar (12-18) */}
              {rankLeaderboard.some(u => u.savwarRank >= 12 && u.savwarRank <= 18) && (
                <>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #C0C0C0 0%, #808080 100%)', 
                    padding: '8px 16px', 
                    borderRadius: 8, 
                    marginBottom: 12,
                    fontWeight: 'bold',
                    color: '#000'
                  }}>
                    🎖️ SUBAYLAR
                  </div>
                  {rankLeaderboard
                    .map((rankUser, index) => ({ ...rankUser, globalIndex: index }))
                    .filter(u => u.savwarRank >= 12 && u.savwarRank <= 18)
                    .map(renderRankUser)}
                </>
              )}

              {/* Astsubaylar (6-11) */}
              {rankLeaderboard.some(u => u.savwarRank >= 6 && u.savwarRank <= 11) && (
                <>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)', 
                    padding: '8px 16px', 
                    borderRadius: 8, 
                    marginBottom: 12,
                    fontWeight: 'bold',
                    color: '#fff'
                  }}>
                    🎯 ASTSUBAYLAR
                  </div>
                  {rankLeaderboard
                    .map((rankUser, index) => ({ ...rankUser, globalIndex: index }))
                    .filter(u => u.savwarRank >= 6 && u.savwarRank <= 11)
                    .map(renderRankUser)}
                </>
              )}

              {/* Uzman Erbaş (4-5) */}
              {rankLeaderboard.some(u => u.savwarRank >= 4 && u.savwarRank <= 5) && (
                <>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #4169E1 0%, #0000CD 100%)', 
                    padding: '8px 16px', 
                    borderRadius: 8, 
                    marginBottom: 12,
                    fontWeight: 'bold',
                    color: '#fff'
                  }}>
                    🔰 UZMAN ERBAŞ
                  </div>
                  {rankLeaderboard
                    .map((rankUser, index) => ({ ...rankUser, globalIndex: index }))
                    .filter(u => u.savwarRank >= 4 && u.savwarRank <= 5)
                    .map(renderRankUser)}
                </>
              )}

              {/* Erbaş (2-3) */}
              {rankLeaderboard.some(u => u.savwarRank >= 2 && u.savwarRank <= 3) && (
                <>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #32CD32 0%, #228B22 100%)', 
                    padding: '8px 16px', 
                    borderRadius: 8, 
                    marginBottom: 12,
                    fontWeight: 'bold',
                    color: '#fff'
                  }}>
                    ⚔️ ERBAŞ
                  </div>
                  {rankLeaderboard
                    .map((rankUser, index) => ({ ...rankUser, globalIndex: index }))
                    .filter(u => u.savwarRank >= 2 && u.savwarRank <= 3)
                    .map(renderRankUser)}
                </>
              )}

              {/* Erler (1) */}
              {rankLeaderboard.some(u => u.savwarRank === 1) && (
                <>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #90EE90 0%, #00FF00 100%)', 
                    padding: '8px 16px', 
                    borderRadius: 8, 
                    marginBottom: 12,
                    fontWeight: 'bold',
                    color: '#000'
                  }}>
                    🟢 ERLER
                  </div>
                  {rankLeaderboard
                    .map((rankUser, index) => ({ ...rankUser, globalIndex: index }))
                    .filter(u => u.savwarRank === 1)
                    .map(renderRankUser)}
                </>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <div style={{ padding: 24, textAlign: 'center' }}>
          <div>⏳ Yükleniyor...</div>
        </div>
        {renderRankModal()}
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ padding: 24 }}>
          <div style={{ color: 'tomato', marginBottom: 16 }}>{error}</div>
          <button onClick={loadActiveSession}>Tekrar Dene</button>
        </div>
        {renderRankModal()}
      </>
    );
  }

  // Bitmiş oturum gösterimi
  if (!session && finishedSession) {
    return (
      <>
        <div style={{ padding: 24, maxWidth: 800, margin: '0 auto', backgroundColor: '#000', minHeight: '100vh' }}>
        {/* Başlık: Kupa ikonu + Sonuçlar */}
        <h2 style={{
          fontSize: 28,
          fontWeight: 'bold',
          color: '#FFD700',
          textAlign: 'center',
          marginBottom: 12,
        }}>
          🏆 Sonuçlar
        </h2>

        {/* Hashtag (tıklanabilir) */}
        <div
          style={{
            fontSize: 24,
            fontWeight: 'bold',
            color: '#114232',
            textAlign: 'center',
            marginBottom: 8,
            cursor: 'pointer',
          }}
          onClick={() => {
            const slug = normalizeSlug(finishedSession.session.topicTag.replace('#', ''));
            navigate(`/savwar/topics/${slug}`);
          }}
        >
          {finishedSession.session.topicTag}
        </div>

        {/* Tarih Aralığı */}
        <div style={{
          fontSize: 14,
          color: '#888',
          textAlign: 'center',
          marginBottom: 16,
        }}>
          {new Date(finishedSession.session.startAt).toLocaleDateString('tr-TR')} - {new Date(finishedSession.session.endAt).toLocaleDateString('tr-TR')}
        </div>

        {/* İstatistikler: Post, Yıldız, Yorum */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 20,
          marginBottom: 24,
        }}>
          <div style={{ fontSize: 16, color: '#114232', fontWeight: '600' }}>
            📝 {finishedSession.session.stats.posts} post
          </div>
          <div style={{ fontSize: 16, color: '#114232', fontWeight: '600' }}>
            ⭐ {finishedSession.session.stats.stars} beğeni
          </div>
          <div style={{ fontSize: 16, color: '#114232', fontWeight: '600' }}>
            💬 {finishedSession.session.stats.commenters} yorumcu
          </div>
        </div>

        {/* Top 3 Başlığı */}
        <h3 style={{
          fontSize: 20,
          fontWeight: 'bold',
          color: '#114232',
          textAlign: 'center',
          marginBottom: 16,
        }}>
          🥇 Top 3 Kazananlar
        </h3>

        {/* Kazanan kartları */}
        {finishedSession.top3.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666' }}>Katılımcı olmadı.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
            {finishedSession.top3.map((post, idx) => {
              const borderColors = ['#FFD700', '#C0C0C0', '#CD7F32']; // Gold, Silver, Bronze
              const borderColor = borderColors[idx];
              
              return (
                <div
                  key={post._id}
                  style={{
                    backgroundColor: '#1a1a1a',
                    borderWidth: 3,
                    border: `3px solid ${borderColor}`,
                    borderRadius: 16,
                    padding: 16,
                    position: 'relative',
                    boxShadow: `0 0 ${8 - idx * 2}px ${borderColor}`,
                  }}
                >
                  {/* Avatar sağ üstte */}
                  <div
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: 12,
                      cursor: 'pointer',
                      border: '2px solid #114232',
                      borderRadius: '50%',
                      overflow: 'hidden',
                    }}
                    onClick={() => navigate(`/u/${post.authorId.username}`)}
                  >
                    <Avatar
                      src={resolveAvatar(post.authorId.avatarUrlSmall)}
                      alt={post.authorId.username}
                      size={50}
                    />
                  </div>

                  {/* Header: Medal + Rütbe + Username + TP */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 32, marginRight: 12 }}>
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                    </div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {post.authorId.savwarRank > 0 && (
                        <div style={{ fontSize: 24 }}>
                          <RankTooltip 
                            rank={post.authorId.savwarRank} 
                            tp={post.authorId.savwarTP} 
                            username={post.authorId.username}
                          />
                        </div>
                      )}
                      <strong
                        style={{ cursor: 'pointer', color: '#fff' }}
                        onClick={() => navigate(`/u/${post.authorId.username}`)}
                      >
                        {post.authorId.username}
                      </strong>
                      {/* User Badge'ler */}
                      <UserBadgeList user={post.authorId} />
                      <span style={{ fontSize: 14, color: '#114232', fontWeight: '600' }}>
                        🗡️ {post.authorId.savwarTP?.toFixed(2)} TP
                      </span>
                    </div>
                  </div>

                  {/* Post içeriği */}
                  <div style={{ marginBottom: 12, color: '#ddd', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {post.content.split(/(#[a-zA-ZçğıöşüÇĞİÖŞÜ0-9_]+)/g).map((part, i) => {
                      if (part.match(/^#[a-zA-ZçğıöşüÇĞİÖŞÜ0-9_]+$/)) {
                        const tag = part.slice(1);
                        const slug = normalizeSlug(tag);
                        return (
                          <span
                            key={i}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/savwar/topics/${slug}`);
                            }}
                            style={{
                              color: '#114232',
                              fontWeight: 600,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            {part}
                          </span>
                        );
                      }
                      return part;
                    })}
                  </div>

                  {/* Stats: Yıldız ve Yorum */}
                  <div style={{ display: 'flex', gap: 16, fontSize: 14, color: '#aaa' }}>
                    <span>⭐ {post.starsCount}</span>
                    <span>💬 {post.commentsCount}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Rütbe Sıralaması Butonu */}
        <button
          onClick={loadRankLeaderboard}
          disabled={loadingRankLeaderboard}
          style={{
            width: '100%',
            padding: '12px 20px',
            marginBottom: 12,
            borderRadius: 8,
            background: '#1a3a1a',
            border: '1px solid #2a6a2a',
            color: '#fff',
            cursor: loadingRankLeaderboard ? 'not-allowed' : 'pointer',
          }}
        >
          {loadingRankLeaderboard ? 'Yükleniyor...' : '🏆 Rütbe Puanına Göre Sıralama'}
        </button>

        {/* Arşiv Butonu */}
        <button
          onClick={() => navigate('/savwar/archive')}
          style={{
            width: '100%',
            padding: '12px 20px',
            borderRadius: 8,
            background: '#1a1a3a',
            border: '1px solid #2a2a6a',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          📚 Geçmiş Oturumlar (Arşiv)
        </button>
      </div>
        {renderRankModal()}
      </>
    );
  }

  if (!session) {
    return (
      <>
        <div style={{ padding: 24, textAlign: 'center' }}>
          <div style={{ marginBottom: 16 }}>Şu anda aktif bir SavWar oturumu yok.</div>
          <button onClick={loadActiveSession}>Yenile</button>
        </div>
        {renderRankModal()}
      </>
    );
  }

  // Aktif oturum gösterimi
  const displayedPosts = posts.slice(0, displayCount);

  return (
    <>
      <div style={{ padding: '16px', maxWidth: 800, margin: '0 auto', backgroundColor: '#000', minHeight: '100vh' }}>
        
        {/* Header: Hashtag + Timer + Stats */}
        <div style={{ 
          padding: '20px 16px', 
          background: '#FFD700',
          borderRadius: 12, 
          marginBottom: 16,
        }}>
          <div 
            style={{ 
              fontSize: 32, 
              fontWeight: 'bold', 
              color: '#000', 
              textAlign: 'center',
              marginBottom: 4,
              margin: 0,
              cursor: 'pointer',
            }}
            onClick={() => {
              const slug = normalizeSlug(session.topicTag.replace('#', ''));
              navigate(`/savwar/topics/${slug}`);
            }}
          >
            {session.topicTag}
          </div>
          
          <div style={{ 
            fontSize: 24, 
            fontWeight: 'bold', 
            color: '#333', 
            textAlign: 'center',
            marginBottom: 12,
          }}>
            ⏱️ {formatTimeRemaining(timeRemaining)}
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 20, 
            fontSize: 14,
            color: '#333',
          }}>
            <span>📝 {session.stats.posts} post</span>
            <span>⭐ {session.stats.stars} beğeni</span>
            <span>💬 {session.stats.commenters} yorumcu</span>
          </div>
        </div>

      {/* Kullanıcı Post Paneli */}
      {user && (
        !userPost ? (
          // Post yazma alanı
        <div style={{
          padding: 16,
          background: '#1a1a1a',
          border: '2px solid #114232',
          borderRadius: 16,
          marginBottom: 16,
        }}>
          <div style={{ 
            fontSize: 14, 
            color: '#888', 
            marginBottom: 8,
          }}>
            Postunuzu yazın (2-200 karakter, {session.topicTag} içermeli)
          </div>
          <textarea
            value={postContent}
            onChange={(e) => {
              let newValue = e.target.value;
              // Hashtag'den sonra boşluk silinmesin
              const tag = session.topicTag;
              const tagWithSpace = `${tag} `;
              if (postContent.includes(tagWithSpace) && !newValue.includes(tagWithSpace)) {
                // Eğer hashtag+boşluk vardı ama yeni değerde yok ise, geri ekle
                if (newValue.includes(tag) && !newValue.includes(tagWithSpace)) {
                  newValue = newValue.replace(tag, tagWithSpace);
                }
              }
              setPostContent(newValue);
            }}
            placeholder={`Örn: ${session.topicTag} ile ilgili düşüncelerinizi paylaşın...`}
            maxLength={200}
            rows={4}
            disabled={!session.active}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #2c2c2c',
              background: '#1a1a1a',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 14,
              resize: 'vertical',
              marginBottom: 12,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={() => {
                  const tag = session.topicTag;
                  if (!postContent.includes(tag)) {
                    setPostContent(prev => (prev ? `${prev} ${tag} ` : `${tag} `));
                  }
                }}
                style={{
                  padding: '6px 12px',
                  background: '#1C542D',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {session.topicTag}
              </button>
              <span style={{ fontSize: 12, color: '#666' }}>
                {postContent.length}/200
              </span>
            </div>
            <button
              onClick={handleSubmitPost}
              disabled={!session.active || !postContent.trim() || submitting}
              style={{
                padding: '8px 16px',
                background: (!session.active || !postContent.trim() || submitting) ? '#ccc' : '#FFD700',
                color: '#000',
                border: 'none',
                borderRadius: 8,
                cursor: (!session.active || !postContent.trim() || submitting) ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {submitting ? 'Gönderiliyor...' : 'Gönder (-10⭐)'}
            </button>
          </div>
        </div>
        ) : (
          // Kullanıcının mevcut postu
          <div style={{
            padding: 16,
            background: '#1a1a1a',
            border: '2px solid #114232',
            borderRadius: 12,
            marginBottom: 16,
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {userPostRank && (
                  <span style={{ 
                    fontSize: 18, 
                    fontWeight: 'bold',
                    color: '#FFD700',
                  }}>
                    #{userPostRank}
                  </span>
                )}
                {userPost.authorId.savwarRank > 0 && (
                  <div style={{ fontSize: 24 }}>
                    <RankTooltip 
                      rank={userPost.authorId.savwarRank} 
                      tp={userPost.authorId.savwarTP} 
                      username={userPost.authorId.username}
                    />
                  </div>
                )}
                <span style={{ 
                  fontSize: 14, 
                  fontWeight: 600,
                  color: '#114232',
                }}>
                  ✅ Sizin Postunuz
                </span>
              </div>
              <span style={{ fontSize: 14, color: '#888' }}>
                🗡️ {userPost.authorId.savwarTP?.toFixed(2) || '0.00'} TP
              </span>
            </div>

            {editMode ? (
              <>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  maxLength={200}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 8,
                    border: '1px solid #2c2c2c',
                    background: '#1a1a1a',
                    color: '#fff',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    resize: 'vertical',
                    marginBottom: 12,
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#666' }}>
                    {editContent.length}/200
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={handleSaveEdit}
                      disabled={submitting}
                      style={{
                        padding: '6px 16px',
                        background: '#114232',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        fontSize: 13,
                      }}
                    >
                      {submitting ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      style={{
                        padding: '6px 16px',
                        background: '#333',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      İptal
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={{ 
                  fontSize: 15, 
                  lineHeight: 1.6,
                  color: '#fff',
                  marginBottom: 12,
                  whiteSpace: 'pre-wrap',
                }}>
                  {renderContentWithHashtags(userPost.content)}
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  paddingTop: 12,
                  borderTop: '1px solid #2c2c2c',
                }}>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <span style={{ fontSize: 14 }}>⭐ {userPost.starsCount}</span>
                    <span style={{ fontSize: 14 }}>💬 {userPost.commentsCount}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {(() => {
                      const postAge = Date.now() - new Date(userPost.createdAt).getTime();
                      const fiveMinutes = 5 * 60 * 1000;
                      const canEdit = postAge < fiveMinutes;
                      
                      return canEdit ? (
                        <button
                          onClick={handleEditPost}
                          style={{
                            padding: '6px 12px',
                            background: '#333',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 12,
                          }}
                        >
                          Düzenle
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={handleDeletePost}
                      style={{
                        padding: '6px 12px',
                        background: '#991b1b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >
                      Sil
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )
      )}

      {/* Leaderboard Başlığı */}
      <h3 style={{ 
        fontSize: 20, 
        fontWeight: 'bold', 
        color: '#114232',
        marginBottom: 16,
        textAlign: 'center',
      }}>
        🏆 Liderlik Tablosu
      </h3>

      {/* Post Kartları */}
      {posts.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#666', padding: 40 }}>
          Henüz post yok. İlk sen ol!
        </div>
      ) : (
        <>
          {displayedPosts.map((post, index) => {
            const isOwnPost = post.authorId._id === user?.id;
            const rankChange = (post as any)._rankChange;
            
            return (
              <div 
                key={post._id}
                style={{
                  padding: 16,
                  background: rankChange === 'up' ? '#0a1a0a' : rankChange === 'down' ? '#1a0a0a' : '#0f0f0f',
                  border: `1px solid ${rankChange === 'up' ? '#14532d' : rankChange === 'down' ? '#7f1d1d' : '#2c2c2c'}`,
                  borderRadius: 12,
                  marginBottom: 12,
                  position: 'relative',
                }}
              >
                {/* Avatar sağ üstte */}
                <div
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: 12,
                    cursor: 'pointer',
                    border: '2px solid #114232',
                    borderRadius: '50%',
                    overflow: 'hidden',
                  }}
                  onClick={() => navigate(`/u/${post.authorId.username}`)}
                >
                  <Avatar
                    src={resolveAvatar(post.authorId.avatarUrlSmall)}
                    alt={post.authorId.username}
                    size={40}
                  />
                </div>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingRight: 60 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 18, fontWeight: 'bold', color: '#FFD700', minWidth: 40 }}>
                      #{index + 1}
                    </span>
                    {rankChange === 'up' && <span style={{ color: '#4ade80', fontSize: 16 }}>▲</span>}
                    {rankChange === 'down' && <span style={{ color: '#f87171', fontSize: 16 }}>▼</span>}
                  </div>
                  
                  {post.authorId.savwarRank > 0 && (
                    <div style={{ fontSize: 20 }}>
                      <RankTooltip 
                        rank={post.authorId.savwarRank} 
                        tp={post.authorId.savwarTP} 
                        username={post.authorId.username}
                      />
                    </div>
                  )}
                  
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer' }}
                    onClick={() => navigate(`/u/${post.authorId.username}`)}>
                    {post.authorId.username}
                  </div>
                  
                  <span style={{ fontSize: 13, color: rankChange === 'up' ? '#4ade80' : rankChange === 'down' ? '#f87171' : '#888', fontWeight: rankChange ? 600 : 400 }}>
                    🗡️ {post.authorId.savwarTP?.toFixed(2) || '0.00'}
                  </span>
                </div>

                {/* İçerik */}
                <div style={{ fontSize: 15, lineHeight: 1.6, color: '#fff', marginBottom: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {renderContentWithHashtags(post.content)}
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', gap: 16, paddingTop: 12, borderTop: '1px solid #2c2c2c' }}>
                  <button
                    onClick={(e) => {
                      // Ripple effect
                      const btn = e.currentTarget;
                      const ripple = document.createElement('div');
                      ripple.className = 'animate-ripple';
                      ripple.style.cssText = `
                        position: absolute;
                        inset: 0;
                        background: radial-gradient(circle, rgba(17, 66, 50, 0.6) 0%, transparent 70%);
                        border-radius: 6px;
                        pointer-events: none;
                      `;
                      btn.style.position = 'relative';
                      btn.appendChild(ripple);
                      setTimeout(() => ripple.remove(), 600);
                      
                      handleToggleStar(post._id);
                    }}
                    disabled={isOwnPost || !session.active}
                    className={post.hasStarred ? 'animate-button-bounce' : ''}
                    style={{
                      padding: '6px 12px',
                      background: post.hasStarred ? '#114232' : 'transparent',
                      color: post.hasStarred ? '#fff' : '#888',
                      border: `1px solid ${post.hasStarred ? '#114232' : '#2c2c2c'}`,
                      borderRadius: 6,
                      cursor: (isOwnPost || !session.active) ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                      opacity: (isOwnPost || !session.active) ? 0.5 : 1,
                      transition: 'all 0.2s',
                    }}
                    onMouseOver={(e) => {
                      if (!isOwnPost && session.active) {
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    {post.hasStarred ? '⭐' : '☆'} {post.starsCount}
                  </button>
                  
                  <button
                    onClick={() => navigate(`/savwar/post/${post._id}`)}
                    style={{
                      padding: '6px 12px',
                      background: 'transparent',
                      color: '#888',
                      border: '1px solid #2c2c2c',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                  >
                    💬 {post.commentsCount}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Daha Fazla */}
          {displayCount < posts.length && (
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{
                width: '100%',
                padding: 16,
                background: '#0f0f0f',
                color: '#114232',
                border: '1px solid #2c2c2c',
                borderRadius: 8,
                cursor: loadingMore ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 16,
              }}
            >
              {loadingMore ? 'Yükleniyor...' : `Daha Fazla Göster (${posts.length - displayCount} Post)`}
            </button>
          )}

          {/* Sonuna ulaşıldı */}
          {displayCount >= posts.length && (
            <div style={{ textAlign: 'center', color: '#666', padding: 16, fontSize: 14 }}>
              🏁 Top {posts.length} gösteriliyor
            </div>
          )}
        </>
      )}

      {/* Rütbe Sıralaması Butonu */}
      <button
        onClick={loadRankLeaderboard}
        style={{
          width: '100%',
          padding: 16,
          background: '#1C542D',
          color: '#fff',
          border: 'none',
          borderRadius: 12,
          cursor: 'pointer',
          fontSize: 16,
          fontWeight: 'bold',
          marginBottom: 12,
        }}
      >
        🏆 Rütbe Puanına Göre Sıralama
      </button>

      {/* Arşiv Butonu */}
      <button
        onClick={() => navigate('/savwar/archive')}
        style={{
          width: '100%',
          padding: 16,
          background: '#0f0f0f',
          color: '#888',
          border: '1px solid #2c2c2c',
          borderRadius: 12,
          cursor: 'pointer',
          fontSize: 16,
          fontWeight: 'bold',
          marginBottom: 40,
        }}
      >
        📚 Geçmiş Oturumlar (Arşiv)
      </button>

      {/* TP Gain Toast */}
      {showTpToast && (
        <TPGainToast tp={tpGained} onClose={() => setShowTpToast(false)} />
      )}

      {/* Rank Up Modal */}
      {showRankUpModal && rankUpData && (
        <RankUpModal
          oldRank={rankUpData.oldRank}
          newRank={rankUpData.newRank}
          onClose={() => {
            setShowRankUpModal(false);
            setRankUpData(null);
          }}
        />
      )}
    </div>

    {renderRankModal()}
    </>
  );
}
